import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Pagination, Progress, Table } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppStack from '../../common/AppStack';
import AppFlexbox from '../../common/AppFlexbox';
import { useRegAssociationBranding } from '../../../helpers/styles';
import AppText from '../../common/AppText';
import AppTitle from '../../common/AppTitle';
import AlertAction from '../../common/AlertAction';
import { useMediaQueryIndex } from '../../../helpers/hooks';
import { abbreviateString } from '../../../helpers/evaluationPlayerHelper';
import PlayerStatsCardSummary from './PlayerStatsCardSummary';

const PlayerStatsSummaryModal = ({
  player,
  regAssociation,
  isOpen,
  onClose
}) => {
  const mqIndex = useMediaQueryIndex();
  const isMobileView = mqIndex === 0;
  const isTabletView = mqIndex <= 1;
  const navigate = useNavigate();
  const STATS_MAX_PER_PAGE = 5;
  const [filterState, setFilterState] = useState({ pageIndex: 1 });
  const {
    primaryColor,
    secondaryColor,
    primaryTextColor,
    secondaryTextColor
  } = useRegAssociationBranding(regAssociation);

  const playerEvaluationRatings =
    player?.regScoutReportPlayerAnswers.map((a) => ({
      ...a,
      abbr: abbreviateString(a.questionLabel, true)
    })) || [];

  useEffect(() => {
    if (isOpen) {
      setFilterState({ pageIndex: 1 });
    }
  }, []);

  return (
    <ResponsiveModal
      onClose={onClose}
      opened={isOpen}
      size={player?.overallScore ? 1000 : 600}
      title={<AppTitle order={2}>Player Stats</AppTitle>}
    >
      {!player || !player.overallScore ? (
        <AlertAction
          actionLabel="Create Session"
          message="No evaluations completed for the selected player. Create a session and start evaluating the player to see their stats."
          onAction={() => {
            navigate('/admin/evaluations/sessions', {
              state: { openModal: true }
            });
          }}
        />
      ) : (
        <AppStack>
          <AppFlexbox
            style={{
              gap: 20,
              flexDirection: isTabletView ? 'column' : 'row'
            }}
          >
            <AppStack
              style={{
                flex: 1,
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <AppStack
                style={{
                  alignSelf: isMobileView ? 'stretch' : 'center',
                  width: isMobileView ? 'unset' : 300
                }}
              >
                <PlayerStatsCardSummary
                  maxPerPage={STATS_MAX_PER_PAGE}
                  pageIndex={filterState.pageIndex}
                  player={player}
                  primaryColor={primaryColor}
                  primaryTextColor={primaryTextColor}
                  secondaryColor={secondaryColor}
                  secondaryTextColor={secondaryTextColor}
                />
              </AppStack>
            </AppStack>
            <AppStack style={{ flex: 1 }}>
              <Table
                striped
                style={{ border: `solid 2px ${primaryColor}`, flex: 1 }}
                withRowBorders={false}
              >
                <Table.Thead>
                  <Table.Tr style={{ backgroundColor: primaryColor }}>
                    <Table.Th colSpan={2}>
                      <AppText
                        style={{ color: primaryTextColor, fontSize: 22 }}
                        weight={500}
                      >
                        Metrics
                      </AppText>
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {playerEvaluationRatings
                    .slice(
                      -STATS_MAX_PER_PAGE +
                        filterState.pageIndex * STATS_MAX_PER_PAGE,
                      filterState.pageIndex * STATS_MAX_PER_PAGE
                    )
                    .map((r) => (
                      <Table.Tr key={r.questionLabel}>
                        <Table.Td style={{ alignContent: 'end' }}>
                          <AppStack style={{ gap: 0, flex: 1 }}>
                            <AppText
                              style={{ textWrap: 'nowrap', fontSize: 18 }}
                              weight={500}
                            >
                              {r.questionLabel} ({r.abbr})
                            </AppText>
                            <AppText
                              style={{
                                color: '#767678',
                                height: 25,
                                fontSize: 18
                              }}
                              weight={500}
                            >
                              {r.rating}
                            </AppText>
                          </AppStack>
                        </Table.Td>
                        <Table.Td
                          style={{ width: '100%', alignContent: 'end' }}
                        >
                          <AppStack
                            style={{ height: 25, justifyContent: 'center' }}
                          >
                            <Progress
                              color={primaryColor}
                              radius="xs"
                              size="xl"
                              value={r.rating * 2 * 10}
                            />
                          </AppStack>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                </Table.Tbody>
              </Table>

              {playerEvaluationRatings.length > STATS_MAX_PER_PAGE && (
                <AppFlexbox style={{ justifyContent: 'center' }}>
                  <Pagination
                    color="blue"
                    onChange={(pageIndex) =>
                      setFilterState({
                        ...filterState,
                        pageIndex
                      })
                    }
                    total={Math.ceil(
                      playerEvaluationRatings.length / STATS_MAX_PER_PAGE
                    )}
                    value={filterState.pageIndex}
                  />
                </AppFlexbox>
              )}
            </AppStack>
          </AppFlexbox>
        </AppStack>
      )}
    </ResponsiveModal>
  );
};

PlayerStatsSummaryModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  player: PropTypes.object,
  regAssociation: PropTypes.object,
  regAssociationDivisionTeam: PropTypes.object,
  regAssociationDivisionTeamPerson: PropTypes.object,
  regScoutReportEvaluationSessions: PropTypes.array
};

export default PlayerStatsSummaryModal;
